import { cookie } from "../lib/cookies.js";
import {
  formDataToDemBaseTrackEventProps,
  trackDemBaseEvent,
} from "./demandbase.js";
import { getSegment } from "./helpers.js";
import {
  copyAndMapObj,
  objectHasValues,
  getGaClientId,
  getUTCTimeZone,
} from "../../src/helpers";

/**
 * @typedef {Record<string,string>} FormData
 */

// /////////////////////////////////////////////////////////////////////////////
// Free trial long and short forms
// /////////////////////////////////////////////////////////////////////////////

/**
 * Map *form field names* to *segment trait keys*
 * See Segment reserved traits:
 * https://segment.com/docs/spec/identify/#traits
 */
var TRAIT_KEYS = {
  first_name: "first_name",
  last_name: "last_name",
  email: "email",
  organization: "company",
};

/**
 * Make a sign-up object from formData
 * @arg {FormData} formData
 * @return {Record<string,any>}
 */
function formDataToIdentifyEventTraits(formData) {
  return copyAndMapObj(TRAIT_KEYS, formData, {});
}

/**
 * Sets additional traits and returns an object that can be sent to Segment.
 * @arg {FormData} formData
 * @return {Record<string,any>}
 */
function setIdentifyTraits(formData) {
  const traits = formDataToIdentifyEventTraits(formData);

  // Get timezone
  const timezone = getUTCTimeZone();
  traits["timezone_name"] = timezone.timeZoneName;
  ((traits["timezone"] = timezone.timeZone),
    // Get Google Analytics Client Id
    (traits["ga_clientid"] = getGaClientId()));

  // Pass the value of the _mkto_trk cookie to tie the identity with munchkin's
  //
  const mktTrk = cookie("_mkto_trk");
  if (mktTrk) traits["mktoTrk"] = mktTrk;

  return traits;
}

/**
 * Issue two segment calls: `identify` with the user provided information and
 * `track` with the enriched DemandBase data.
 * @arg {FormData} formData
 * @return {void}
 */
export function identifyFreeTrialShortForm(formData) {
  var traits = setIdentifyTraits(formData);
  if (objectHasValues(traits)) {
    // Add the `name` field by combining `first_name` and `last_name`.
    // This is also done by marketo identify calls and simplifies reporting
    traits["name"] = traits["first_name"] + " " + traits["last_name"];
    getSegment("identify")(traits);
  }
  var props = formDataToDemBaseTrackEventProps(formData);
  if (objectHasValues(props)) trackDemBaseEvent(props);
}

/**
 * Issue two segment calls: `identify` with the user provided information and
 * `track` with the enriched DemandBase data.
 * @arg {FormData} formData
 * @return {void}
 */
export function identifyFreeTrialLongForm(formData) {
  var traits = setIdentifyTraits(formData);
  if (formData != null && formData["phone_number"] != null) {
    traits["phone"] =
      (formData["mobile_country_code"] ?? "") + formData["phone_number"];
  }
  getSegment("identify")(traits);
  var props = formDataToDemBaseTrackEventProps(formData);
  if (objectHasValues(props)) trackDemBaseEvent(props);
}

// /////////////////////////////////////////////////////////////////////////////
// Generalized event tracking
// /////////////////////////////////////////////////////////////////////////////

/**
 * Returns the tracked element type based on the given event name.
 *
 * @param {string} eventName - The name of the event.
 * @returns {string} The tracked element type.
 */
const getTrackedElementType = (eventName) => {
  switch (eventName) {
    case "Button Clicked":
      return "button";
    case "Card Clicked":
      return "card";
    case "Table Row Expanded":
      return "table_row";
    default:
      return "default";
  }
};

/**
 * Returns an array of allowed properties for a given element type.
 *
 * @param {string} elementType - The type of the element.
 * @returns {string[]} - An array of allowed properties.
 */
const getEventAllowedProperties = (elementType) => {
  let commonProperties = ["organism"];

  switch (elementType) {
    case "button":
      return commonProperties.concat([
        "href",
        "iconName",
        "primary",
        "text",
        "variant",
        "interactive_element",
      ]);
    case "card":
      return commonProperties.concat(["href", "type", "assetType", "title"]);
    case "table_row":
      return commonProperties.concat(["index"]);
    default:
      return commonProperties;
  }
};

/**
 * Parse component tracking props from markup.
 *
 * CMS-authored and legacy templates can contain non-JSON `data-track-props`
 * values. Component tracking expects an object, so malformed values should not
 * break the user's click handler or create Bugsnag noise.
 *
 * @param {string} eventProps
 * @returns {Record<string, any>}
 */
export function parseComponentTrackProps(eventProps) {
  if (!eventProps) return {};

  try {
    const parsed = JSON.parse(eventProps);
    if (
      parsed != null &&
      typeof parsed === "object" &&
      !Array.isArray(parsed)
    ) {
      return parsed;
    }
  } catch (_error) {
    // Fall through to the safe default below.
  }

  return {};
}

/**
 * @param {Node} element
 */
const hasTrackAttribute = (element) =>
  element instanceof HTMLElement &&
  element.hasAttribute("data-track-event") &&
  element.dataset["trackEvent"];

/**
 * @param {MutationRecord[]} mutationList
 */
function observeMutationsForTrackEvents(mutationList) {
  mutationList.forEach((mutation) => {
    if (
      mutation.target &&
      mutation.target instanceof Element &&
      hasTrackAttribute(mutation.target)
    ) {
      setupTrackHandler(mutation.target);
    }
    // Check for track elements in addedNodes and their descendants
    Array.from(mutation.addedNodes).forEach((node) => {
      if (node instanceof Element) {
        // Setup track handler for the node itself
        hasTrackAttribute(node) && setupTrackHandler(node);

        // Setup track handler for all its descendants
        const descendants = Array.from(
          node.querySelectorAll("[data-track-event]"),
        );
        for (const child of descendants) {
          hasTrackAttribute(child) && setupTrackHandler(child);
        }
      }
    });
  });
}

export function setupTrackClickEventsObserver() {
  const observer = new MutationObserver(observeMutationsForTrackEvents);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

/**
 * Sets up or re-attaches a click event listener for a DOM element with a `data-track-event` attribute.
 * This function ensures that the appropriate event handler is bound to the element, based on the value
 * of its `data-track-event` attribute. It is typically called during DOM mutations to handle dynamically
 * added or updated elements.
 *
 * The function first removes any existing click event listener to prevent duplicate bindings, then
 * attaches a new listener. The event handler is determined by the `data-track-event` value.
 *
 * @param {Element} element - A DOM element expected to have a `data-track-event` attribute.
 * @returns {void}
 */
function setupTrackHandler(element) {
  if (!element || !(element instanceof HTMLElement)) return;

  const event = element.getAttribute("data-track-event")?.trim();
  if (!event) return;

  let handler;
  let options = {};
  switch (event) {
    case "Button Clicked":
    case "Card Clicked":
      handler = trackComponentEventHandler;
      break;
    case "Table Row Expanded":
      handler = trackComponentEventHandler;
      options = { once: true };
      break;
    default:
      handler = trackEventHandler;
  }

  element.removeEventListener("click", handler);
  element.addEventListener("click", handler, options);
}

export function setupTrackClickEvents() {
  const trackEvents = document.querySelectorAll("[data-track-event]");
  for (const element of trackEvents) {
    setupTrackHandler(element);
  }
}

/**
 * Since some generic event buttons sit inside injected HTML
 * you need to add an extra class to those buttons and call this
 * function after the HTML has been injected.
 */
export function setupABTestTrackClickEvents() {
  const trackEvents = document.querySelectorAll(".abtest-generic-event");
  for (let i = 0; i < trackEvents.length; ++i) {
    trackEvents[i]?.addEventListener("click", trackEventHandler);
  }
}

/**
 * Track generic events. The events are described in markup by two attributes:
 * `data-track-event` -> string with the eventType.
 * `data-track-props` -> space separated string with relevant properties, which
 * will be converted to an array of strings.
 * @param {Event} e
 */
export function trackEventHandler(e) {
  const target = e.currentTarget;
  if (target instanceof HTMLElement) {
    const eventType = target?.dataset["trackEvent"] ?? "undefined";
    const eventProps = target?.dataset["trackProps"];
    const eventProperties = eventProps ? eventProps.split(/\s+/) : [];
    const text = target?.innerText;
    trackGenericEvent(eventType, eventProperties, text);
  }
}

/**
 * Track atom events. The events are described in markup by two attributes:
 * `data-track-event` -> string with the event name
 * `data-track-props` -> object with relevant properties
 * @param {Event} e
 */
function trackComponentEventHandler(e) {
  const target = e.currentTarget;
  /**
   * @type {{ [x: string]: any; organism?: any; href?: any; attributes?: any; video?: any; interactive_element?: any; text?: any; }}
   */
  let eventProperties = {};

  if (target instanceof HTMLElement) {
    const eventName = target?.dataset["trackEvent"] ?? "Unnamed Event";
    const eventProps = target?.dataset["trackProps"] ?? "{}";
    const elementType = getTrackedElementType(eventName);

    if (eventProps) {
      eventProperties = parseComponentTrackProps(eventProps);
      eventProperties.organism = /** @type {string} */ (
        target
          ?.closest("[data-track-organism]")
          ?.getAttribute("data-track-organism")
      );
      if (target?.getAttribute("href")) {
        eventProperties.href = target?.getAttribute("href");
      }
      if (target?.innerText) {
        eventProperties.text = target?.innerText.trim();
      }
      if (eventProperties.video) {
        eventProperties.interactive_element = "video";
      } else if (
        target?.dataset["addonModalId"] ||
        target?.classList.contains("btn-show-modal")
      ) {
        eventProperties.interactive_element = "modal";
      }
      if (eventProperties["assetType"]) {
        eventProperties["type"] = "resource";
      }

      const allowedProperties = getEventAllowedProperties(elementType);

      Object.keys(eventProperties)
        .filter((p) => !allowedProperties.includes(p))
        .forEach((p) => delete eventProperties[p]);
    }

    getSegment("track")(eventName, eventProperties);
  }
}

/**
 * Track generic events.
 * @arg {string} eventType
 * @arg {string[]} eventProperties
 * @arg {string} text
 * @return {void}
 */
export function trackGenericEvent(eventType, eventProperties, text) {
  const properties = {
    event_type: eventType,
    event_properties: eventProperties,
    text: text,
  };
  getSegment("track")("Generic Event", properties);
}

/**
 * Track Pricing List.
 * @arg {string[]} properties
 * @return {void}
 */
export function pricingListViewed(properties) {
  const props = {
    category: "pricing_plan",
    list_id: "account settings",
    products: properties,
  };
  getSegment("track")("Marketing Product List Viewed", props);
}
